/**
 * Bollinger Bands Quantum — renderer.
 *
 * Solo pinta: toda la matemática vive en engine.js. Todo el dibujo son series
 * nativas de lightweight-charts (line series + custom series en canvas) —
 * nada de DOM posicionado sobre el chart (regla C.2) y nada de cleanup propio
 * (el teardown por __indicatorId lo hace la página).
 */

import { LineStyle } from 'lightweight-charts'
import { LabelOverlaySeries } from '../ict_quantum/LabelOverlaySeries.js'
import { BandFillSeries } from './BandFillSeries.js'
import {
  resolveConfig,
  resolveColors,
  fillAlphaForBandwidth,
  withAlpha,
  LABEL_FONT,
} from './constants.js'

// Filtrar NaN, ordenar por time y deduplicar times antes de cualquier setData
// (mismo patrón que cleanValues/cleanCloudData de supertrend_quantum).
function cleanValues(arr) {
  if (!Array.isArray(arr)) return []
  const sorted = arr
    .filter(d => d && d.time != null && d.time !== '' && !Number.isNaN(d.time) && Number.isFinite(d.value))
    .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
  const out = []
  for (const d of sorted) {
    if (out.length > 0 && out[out.length - 1].time === d.time) out[out.length - 1] = d
    else out.push(d)
  }
  return out
}

function cleanBandData(arr) {
  if (!Array.isArray(arr)) return []
  const sorted = arr
    .filter(d => d && d.time != null && d.time !== '' && !Number.isNaN(d.time) && Number.isFinite(d.upper) && Number.isFinite(d.lower))
    .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
  const out = []
  for (const d of sorted) {
    if (out.length > 0 && out[out.length - 1].time === d.time) out[out.length - 1] = d
    else out.push(d)
  }
  return out
}

/** Decimales razonables según magnitud del precio. */
function formatPrice(v) {
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 1000) return v.toFixed(1)
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

const COMMON_LINE_OPTS = {
  lastValueVisible: false,
  priceLineVisible: false,
  crosshairMarkerVisible: false,
  autoscaleInfoProvider: () => null,
}

export function renderBollinger(chart, candleSeries, result, config, api) {
  if (!result || !api) return
  const cfg = resolveConfig(config)
  if (cfg.enabled === false) return
  const points = Array.isArray(result.points) ? result.points : []
  if (points.length === 0) return
  const colors = resolveColors(cfg)

  // ── Relleno entre bandas (debajo de las líneas: se crea primero) ─────────
  if (cfg.showFill) {
    const fillSeries = api.addCustomSeries(new BandFillSeries(), {
      color: 'rgba(0,0,0,0)',
      fillColor: withAlpha(colors.fill, 0.10),
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
    })
    if (fillSeries) {
      const fillData = points.map(p => ({
        time: p.time,
        upper: p.upper,
        lower: p.lower,
        // Alpha por punto modulado por bandwidth: más opaco con bandas
        // anchas, más tenue en squeeze.
        color: withAlpha(colors.fill, fillAlphaForBandwidth(p.bandwidth, cfg.squeezeThreshold)),
      }))
      fillSeries.setData(cleanBandData(fillData))
    }
  }

  // ── Las 3 líneas de banda ────────────────────────────────────────────────
  const bands = [
    { key: 'upper', title: 'BBQ Upper', color: colors.upper, show: cfg.showUpper, lineStyle: LineStyle.Solid },
    { key: 'middle', title: 'BBQ Middle', color: colors.middle, show: cfg.showMiddle, lineStyle: cfg.middleLineStyle === 'dotted' ? LineStyle.Dotted : LineStyle.Solid },
    { key: 'lower', title: 'BBQ Lower', color: colors.lower, show: cfg.showLower, lineStyle: LineStyle.Solid },
  ]
  for (const band of bands) {
    if (!band.show) continue
    const s = api.addSeries(c => c.addLineSeries({
      title: band.title,
      color: band.color,
      lineWidth: cfg.lineWidth,
      lineStyle: band.lineStyle,
      ...COMMON_LINE_OPTS,
    }))
    if (s) s.setData(cleanValues(points.map(p => ({ time: p.time, value: p[band.key] }))))
  }

  // ── Squeeze: línea dotted dorada sobre la banda media en los tramos ──────
  if (cfg.showSqueeze && Array.isArray(result.squeezeSegments) && result.squeezeSegments.length > 0) {
    const squeezeData = []
    for (const seg of result.squeezeSegments) {
      for (const p of points) {
        if (p.time >= seg.fromTime && p.time <= seg.toTime) {
          squeezeData.push({ time: p.time, value: p.middle })
        }
      }
    }
    const cleaned = cleanValues(squeezeData)
    if (cleaned.length > 0) {
      const s = api.addSeries(c => c.addLineSeries({
        title: 'BBQ Squeeze',
        color: colors.squeeze,
        lineWidth: cfg.lineWidth,
        lineStyle: LineStyle.Dotted,
        ...COMMON_LINE_OPTS,
      }))
      if (s) s.setData(cleaned)
    }
  }

  // ── Etiquetas de las 3 bandas en el último punto (texto plano, sin fondo) ─
  if (cfg.showLabels) {
    const last = points[points.length - 1]
    const labelSeries = api.addCustomSeries(new LabelOverlaySeries(), {
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
      plaqueStyle: 'plain',
      font: LABEL_FONT,
    })
    if (labelSeries) {
      labelSeries.setData([{
        time: last.time,
        labels: [
          { price: last.upper, text: `Upper ${formatPrice(last.upper)}`, color: colors.upper, textColor: colors.upper, anchor: 'above', offset: 6, priority: 10, plaqueStyle: 'plain' },
          { price: last.middle, text: `Middle ${formatPrice(last.middle)}`, color: colors.middle, textColor: colors.middle, anchor: 'above', offset: 6, priority: 9, plaqueStyle: 'plain' },
          { price: last.lower, text: `Lower ${formatPrice(last.lower)}`, color: colors.lower, textColor: colors.lower, anchor: 'below', offset: 6, priority: 8, plaqueStyle: 'plain' },
        ],
      }])
    }
  }
}
